// PlanShared.js - Shared components and utilities for Plan Management
import React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE, getHeaders } from '../../config/api';

const API_BASE_URL = `${API_BASE}/api/plans`;



const toUiPlan = (plan) => ({ ...plan, id: plan._id });

const toApiPayload = (plan) => ({
    name: plan.name,
    description: plan.description,
    price: plan.price,
    planType: plan.planType || 'monthly',
    isDiscountApplicable: Number(plan.discountAmount) > 0,
    discountType: plan.discountType,
    discountAmount: plan.discountAmount,
    features: plan.features,
    status: plan.status || 'active'
});

const extractErrorMessage = (err) =>
    err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.';


export const PlanService = {
    getAll: async () => {
        try {
            const { data } = await axios.get(`${API_BASE_URL}/getall-plans`, { headers: getHeaders() });
            return (data.plans || []).map(toUiPlan);
        } catch (err) {
            const errorMsg = extractErrorMessage(err);
            toast.error(errorMsg);
            throw new Error(errorMsg);
        }
    },

    getById: async (id) => {
        try {
            const plans = await PlanService.getAll();
            const plan = plans.find(p => p.id === id) || null;
            if (!plan) {
                toast.error('Plan not found');
            }
            return plan;
        } catch (err) {
            const errorMsg = extractErrorMessage(err);
            toast.error(errorMsg);
            throw new Error(errorMsg);
        }
    },

    create: async (plan) => {
        try {
            const { data } = await axios.post(`${API_BASE_URL}/addplan`, toApiPayload(plan), { headers: getHeaders() });
            toast.success('Plan created successfully!');
            return toUiPlan(data.plan);
        } catch (err) {
            const errorMsg = extractErrorMessage(err);
            toast.error(errorMsg);
            throw new Error(errorMsg);
        }
    },

    update: async (id, updates) => {
        try {
            const { data } = await axios.put(`${API_BASE_URL}/updateplan/${id}`, toApiPayload(updates), { headers: getHeaders() });
            toast.success('Plan updated successfully!');
            return toUiPlan(data.plan);
        } catch (err) {
            const errorMsg = extractErrorMessage(err);
            toast.error(errorMsg);
            throw new Error(errorMsg);
        }
    },

    delete: async (id) => {
        try {
            const { data } = await axios.delete(`${API_BASE_URL}/deleteplan/${id}`, { headers: getHeaders() });
            toast.success('Plan deleted successfully!');
            return data;
        } catch (err) {
            const errorMsg = extractErrorMessage(err);
            toast.error(errorMsg);
            throw new Error(errorMsg);
        }
    }
};


export const generateMockUsersForPlans = (plans) => {
    if (localStorage.getItem('planUsers') || !plans.length) return;
    const users = [];
    const statuses = ['active', 'active', 'active', 'active', 'inactive'];
    const names = ['Raj Kumar', 'Priya Sharma', 'Amit Patel', 'Sneha Reddy', 'Vikram Singh', 'Meera Joshi', 'Arjun Nair', 'Divya Menon', 'Kiran Bose', 'Anita Desai'];
    const companies = ['TechCorp', 'InnovateLabs', 'CloudSolutions', 'DataDrive', 'ApexSystems'];
    const roles = ['Admin', 'Manager', 'Developer', 'Designer', 'Analyst', 'Executive'];

    plans.forEach(plan => {
        const count = Math.floor(Math.random() * 50) + 10;
        for (let i = 0; i < count; i++) {
            const status = statuses[Math.floor(Math.random() * statuses.length)];
            users.push({
                id: `user-${Date.now()}-${i}-${plan.id}`,
                planId: plan.id,
                name: names[Math.floor(Math.random() * names.length)],
                email: `user${i}@${companies[Math.floor(Math.random() * companies.length)].toLowerCase()}.com`,
                company: companies[Math.floor(Math.random() * companies.length)],
                role: roles[Math.floor(Math.random() * roles.length)],
                status: status,
                joinedDate: new Date(Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000).toISOString(),
                lastActive: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
                deviceCount: Math.floor(Math.random() * 5) + 1,
                avatar: `https://ui-avatars.com/api/?name=${names[Math.floor(Math.random() * names.length)].replace(' ', '+')}&background=random`
            });
        }
    });
    localStorage.setItem('planUsers', JSON.stringify(users));
};

export const attachSubscriberStats = (plan) => {
    const allUsers = JSON.parse(localStorage.getItem('planUsers') || '[]');
    return {
        ...plan,
        activeUsers: allUsers.filter(u => u.planId === plan.id && u.status === 'active').length,
        inactiveUsers: allUsers.filter(u => u.planId === plan.id && u.status === 'inactive').length,
        subscriberCount: allUsers.filter(u => u.planId === plan.id).length
    };
};


export const Panel = ({ children, className = "" }) => (
    <div
        className={`rounded-2xl p-4 sm:p-5 transition-all bg-white ${className}`}
        style={{
            border: '1px solid rgba(255,125,56,0.2)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
        }}
        onMouseEnter={e => {
            e.currentTarget.style.borderColor = 'rgba(255,125,56,0.4)';
            e.currentTarget.style.boxShadow = '0 8px 30px rgba(255,125,56,0.12)';
        }}
        onMouseLeave={e => {
            e.currentTarget.style.borderColor = 'rgba(255,125,56,0.2)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.06)';
        }}
    >
        {children}
    </div>
);

export const StatCard = ({ label, value, icon: Icon, grad }) => (
    <div className="relative overflow-hidden rounded-2xl p-4 flex items-center gap-3 transition-all hover:scale-105 bg-white border border-orange-200 shadow-sm hover:shadow-lg">
        <div className="absolute -top-8 -left-8 w-20 h-20 rounded-full bg-orange-400/10" />
        <div className="absolute -top-4 -left-4 w-28 h-28 rounded-full border border-orange-300/20" />
        <div className="absolute -top-8 -left-8 w-36 h-36 rounded-full border border-orange-300/10" />
        <div className="absolute -bottom-4 -right-4 w-12 h-12 rounded-full bg-orange-400/10" />

        <div className={`relative z-10 w-9 h-9 rounded-xl bg-gradient-to-br ${grad} flex items-center justify-center shrink-0 shadow-lg`}>
            <Icon size={15} className="text-white" />
        </div>

        <div className="relative z-10 min-w-0">
            <p className="text-[10px] font-medium truncate text-gray-500">{label}</p>
            <p className="text-lg sm:text-xl font-bold truncate text-orange-500">{value}</p>
        </div>
    </div>
);

export const StatusBadge = ({ status }) => (
    <button
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${status === 'active' ? 'bg-orange-50 text-orange-500' : 'bg-gray-100 text-gray-500'}`}
    >
        <span className={`w-1.5 h-1.5 rounded-full ${status === 'active' ? 'bg-orange-500 animate-pulse' : 'bg-gray-400'}`} />
        {status}
    </button>
);

export const PlanTypeBadge = ({ planType }) => (
    <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${planType === 'yearly' ? 'bg-amber-50 text-amber-600 border border-amber-200' : 'bg-orange-50 text-orange-500 border border-orange-200'}`}
    >
        {planType === 'yearly' ? 'Yearly' : 'Monthly'}
    </span>
);

export const ErrorBanner = ({ message, onRetry }) => (
    <div className="flex items-center justify-between gap-3 rounded-xl px-4 py-3 bg-red-50 border border-red-200">
        <p className="text-sm text-red-600">{message}</p>
        {onRetry && (
            <button onClick={onRetry} className="text-xs font-medium text-red-600 hover:underline shrink-0">
                Retry
            </button>
        )}
    </div>
);